import { defineComponent, h, Transition, type PropType } from "vue";
import {
  ToastNotificationClaimPoints,
  ToastNotificationContainer,
  ToastNotificationRankingPosition,
} from "./toast-notification-container";
import { cssClass, ThemeProvider } from "@vue-styled-components/core";
import { theme } from "@/utils/theme";
import { SlideTransition } from "./slide-transition";
import { ToastNotification } from "./toast-notification";
import { AuthenticatingSlate } from "./authenticating-slate";

export interface ToastNotificationItem {
  id: string;
  message: string;
  points?: number;
  duration?: number;
  actionLabel?: string;
  actionCallback?: () => void;
}

export interface ToastNotificationCenterState {
  hasUser: boolean;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  showToasts: boolean;
}

const fadeTransitionClass = cssClass`
  &.toast-enter-from {
    opacity: 0;
    transform: scale(0.9);
  }

  &.toast-enter-to,
  &.toast-leave-from {
    opacity: 1;
    transform: scale(1);
  }

  &.toast-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }
  &.toast-enter-active,
  &.toast-leave-active {
    transition: opacity 150ms ease-in, transform 150ms ease-in;
  }
`;

// Container for all toasts
const toastNotificationCenterClass = cssClass`
  font-family: ${theme?.fonts?.sans};
  position: fixed;
  bottom: 0;
  left: 1.125rem;
  right: 1.125rem;
  z-index: 99999999999999;
  transform: translate(0, 0);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 27rem;
`;

export const ToastNotificationCenter = defineComponent({
  setup: (props) => {
    const toasts = props.toasts;

    return () =>
      h(ThemeProvider, { theme }, () =>
        h(
          "div",
          { class: toastNotificationCenterClass },
          h(SlideTransition, {}, () => {
            if (
              !props.state.showToasts ||
              (toasts.length === 0 && !props.state.isAuthenticating)
            ) {
              return false;
            }

            return h(
              ToastNotificationContainer,
              {
                points: toasts.reduce((a, b) => a + (b.points ?? 0), 0),
                onClose: props.cleanToasts,
              },
              {
                actions: () => {
                  if (props.state.isAuthenticating) {
                    return h(AuthenticatingSlate);
                  }

                  if (props.state.hasUser) {
                    return h(ToastNotificationRankingPosition, {
                      currentPoints: toasts.reduce(
                        (a, b) => a + (b.points ?? 0),
                        0,
                      ),
                      targetPoints: 1000,
                    });
                  }

                  return h(ToastNotificationClaimPoints, {
                    points: toasts.reduce((a, b) => a + (b.points ?? 0), 0),
                    label: props.state.isAuthenticated
                      ? "Finish sign up in FanFest"
                      : "Sign up to claim",
                    onAction: () => {
                      if (props.state.isAuthenticated) {
                        props.goRewards?.();
                      } else {
                        props.goLogin?.();
                      }
                    },
                  });
                },
                default: props.state.isAuthenticating
                  ? null
                  : () =>
                      h(
                        Transition,
                        {
                          name: "toast",
                          mode: "out-in",
                          class: fadeTransitionClass,
                        },
                        () => {
                          const toast = toasts.at(-1)!;
                          return h(
                            ToastNotification,
                            {
                              key: toast.id,
                              points: toast.points ?? 0,
                            },
                            () => toast.message,
                          );
                        },
                      ),
              },
            );
          }),
        ),
      );
  },
  props: {
    state: {
      type: Object as PropType<ToastNotificationCenterState>,
      required: true,
    },
    toasts: {
      type: Array as PropType<ToastNotificationItem[]>,
      required: true,
    },
    cleanToasts: {
      type: Function as PropType<() => void>,
      required: true,
    },
    goLogin: {
      type: Function as PropType<() => void>,
      required: false,
    },
    goRewards: {
      type: Function as PropType<() => void>,
      required: false,
    },
  },
});
